import React from 'react';
import { Button, List, ListItem, ListItemIcon, ListItemText, Switch } from '@mui/material';
import { type GridColDef, type GridColumnVisibilityModel } from '@mui/x-data-grid-premium';

import useSettings from 'app/main/components/hooks/useSettings';
import ResponsiveDialog from 'app/main/components/dialogs/ResponsiveDialog';

interface ViewTableColumnsVisibilityDialogProps {
	open: boolean;
	closeDialog: VoidFunction;
	columns: GridColDef[];
	columnVisibilityModel: GridColumnVisibilityModel;
	setColumnVisibilityModel: (newValue: GridColumnVisibilityModel) => void;
}

export default function ViewTableColumnsVisibilityDialog(props: ViewTableColumnsVisibilityDialogProps) {
	const { t } = useSettings();

	const columns: GridColDef[] = props.columns
		.filter(column => column.type !== 'actions')
		.map(column => ({
			...column,
			hideable: 'hideable' in column ? column.hideable : true
		}));

	function isColumnVisible(column: string) {
		return column in props.columnVisibilityModel ? props.columnVisibilityModel[column] : true;
	}

	function handleSwitchChange(column: string, checked: boolean) {
		props.setColumnVisibilityModel({ ...props.columnVisibilityModel, [column]: checked });
	}

	function handleSwitchAll(checked: boolean) {
		props.setColumnVisibilityModel({
			...Object.fromEntries(columns.filter(column => column.hideable).map(column => [column.field, checked]))
		});
	}

	return (
		<ResponsiveDialog
			open={props.open}
			onClose={props.closeDialog}
			maxWidth="xs"
			title={t('VIEW_TABLE_BUTTON_COLUMN_VISIBILITY')}
			content={
				<List disablePadding>
					{columns.map(column => (
						<ListItem key={column.field} disablePadding>
							<ListItemIcon>
								<Switch
									checked={isColumnVisible(column.field)}
									onChange={event => handleSwitchChange(column.field, event.target.checked)}
									disabled={!column.hideable}
									edge="start"
									disableRipple
								/>
							</ListItemIcon>
							<ListItemText primary={column.headerName} />
						</ListItem>
					))}
				</List>
			}
			actions={
				<>
					<Button onClick={() => handleSwitchAll(true)}>
						{t('VIEW_TABLE_BUTTON_COLUMN_VISIBILITY_SHOW_ALL')}
					</Button>

					<Button onClick={() => handleSwitchAll(false)}>
						{t('VIEW_TABLE_BUTTON_COLUMN_VISIBILITY_HIDE_ALL')}
					</Button>
					<Button onClick={props.closeDialog}>{t('DIALOG_ACTION_CLOSE')}</Button>
				</>
			}
		/>
	);
}
